/**
 * How a report basket reads.
 *
 * Pure functions with tests, the treatment `route.ts`, `views.ts`, `alerts.ts`
 * and `admin.ts` get, and for the same reason: this is the screen where
 * somebody decides to spend money, so the wording *is* the decision.
 *
 * Two rules hold this module together.
 *
 * **No number here is invented.** Every rate, the minimum and the credit
 * balance arrive from the server — `/api/pricing` and `/api/reports/quote` —
 * because the basket a buyer reads and the amount they are charged must come
 * out of one table. That is `WEIGHTS` / `/api/scoring`, and the reason
 * `max_export_rows` was served rather than written into the pricing page.
 *
 * **The note says what the county can evidence, not what the band does to the
 * rates.** It used to end "Rates here are 150% of the standard", and the
 * trouble was the reference point: nothing on this screen defines the
 * standard, and only two of the twenty-seven live counties are at it — so a
 * sentence meant to explain a deep county read as a surcharge over a baseline
 * almost nobody occupies. Removed on 4 Sep 2026 rather than reworded.
 *
 * What survives is the half that was doing the work: a statement about how
 * much the county can evidence, which `scoreScope.ts` already makes on the
 * dashboard. **The percentage is not gone, only unstated here** — the band is
 * still applied to every rate and `guides/pricing.md` still publishes the
 * whole ladder, pinned to `DEPTH_BANDS` by a test. So the cost of this is
 * known and accepted: a buyer reading $0.90 a building against the guide's
 * $0.60 has to leave the basket to reconcile them. `band_percent` is still on
 * the quote and rendered nowhere, which is where a reinstatement starts.
 */

import type { Quote, QuoteLine } from "../api/types";

/** Cents as money. Whole dollars lose the `.00`; everything else keeps it. */
export function money(cents: number): string {
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars.toLocaleString()}` : `$${dollars.toFixed(2)}`;
}

/** A per-record rate, which is usually a fraction of a cent's worth of dollars. */
export function rate(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * "1,204 parcels × $0.04 = $48.16" — the arithmetic, not just the answer.
 *
 * The building line counts something different from what it delivers, and both
 * numbers matter: a buyer is charged per signal-carrying apartment and
 * receives buildings. So it reads "599 buildings · 1,912 apartments carrying a
 * signal × $0.02", which is still a multiplication that comes out — the cap is
 * a separate row rather than a silent reduction of this one.
 */
export function lineSummary(line: QuoteLine): string {
  return (
    `${line.count.toLocaleString()} ${countedNoun(line)} × ` +
    `${rate(line.unit_cents)} = ${money(line.subtotal_cents)}`
  );
}

/** What the line's `count` is counting, singular where there is one of it. */
function countedNoun(line: QuoteLine): string {
  if (line.count !== 1) return line.label.toLowerCase();
  return line.label.toLowerCase().replace(/s$/, "");
}

/**
 * Why a very large basket stopped at one price.
 *
 * The ceiling is the point at which a subscription is the better purchase, so
 * the sentence says that rather than presenting a discount — somebody about to
 * spend the ceiling on one document should know the alternative exists.
 */
export function ceilingNote(quote: Quote): string {
  // Two conditions rather than one, and the second is not redundant: a basket
  // under a configured ceiling and a basket with no ceiling at all both take
  // no discount, but only the first has a figure to name. Reading the amount
  // off a null would render "$NaN ceiling" on the screen where somebody
  // decides to pay.
  if (quote.ceiling_discount_cents <= 0 || quote.ceiling_cents === null) return "";
  return (
    `This basket reaches the ${money(quote.ceiling_cents)} ceiling on a single ` +
    `report. Above it a subscription covers the same county every month for ` +
    `less, so nothing further is charged.`
  );
}

export interface ReportReadiness {
  allowed: boolean;
  /** Why not, for the button's title. Empty when allowed. */
  reason: string;
}

const READY: ReportReadiness = { allowed: true, reason: "" };

/**
 * Whether this basket can be bought, decided before the button is clicked.
 *
 * `exportReadiness`'s replacement and its structural twin: the server refuses
 * rather than trimming, and a refusal somebody only discovers after clicking is
 * a dead end dressed as a button.
 *
 * There is no longer a minimum to fail. Its predecessor refused any basket
 * under the processor's floor, which made the sharpest list in the product
 * unbuyable — the eleven Miami-Dade buildings both overdue for a milestone
 * inspection and carrying an open unsafe-structure case came to 50 cents. The
 * document fee does that job by being charged rather than by refusing.
 */
export function reportReadiness(quote: Quote | null): ReportReadiness {
  if (!quote) return { allowed: false, reason: "Working out what this costs…" };
  if (quote.record_count <= 0) {
    return { allowed: false, reason: "Nothing matches these filters yet." };
  }
  // Refused before the click, with the two numbers that make it actionable.
  // The server refuses this too — a client that forgot would get a 400 rather
  // than an over-long document — but discovering it after paying is a
  // different thing from discovering it before.
  if (quote.too_large) {
    return {
      allowed: false,
      reason:
        `${quote.record_count.toLocaleString()} records match and one report ` +
        `holds at most ${quote.max_records.toLocaleString()}. Narrow it with ` +
        `the score slider, a signal, or a search.`,
    };
  }
  return READY;
}

/**
 * What the buyer is actually paying, in one line.
 *
 * Four states, and the two zeroes are not the same zero. A subscriber's basket
 * is *covered by the plan they already pay for*, which is the sentence that
 * makes a subscription feel like it did something; an internal account is not
 * charged at all and has spent no allowance, so saying "covered by your plan"
 * there would be a claim about an allowance nothing touched.
 */
export function dueSummary(quote: Quote): string {
  if (quote.record_count <= 0) return "Nothing to price yet.";
  // Before the covered-by-plan branch, because the two are different reasons
  // for the same zero and an internal account has not spent an allowance.
  if (quote.waived) {
    return quote.waived_reason || "No charge on this account.";
  }
  if (quote.due_cents === 0) {
    return (
      `Covered by your plan — ${quote.credits_applied.toLocaleString()} of ` +
      `${quote.credits_available.toLocaleString()} records included this month.`
    );
  }
  if (quote.credits_applied > 0) {
    return (
      `${money(quote.due_cents)} after ` +
      `${quote.credits_applied.toLocaleString()} records covered by your plan.`
    );
  }
  return `${money(quote.due_cents)} for ${quote.record_count.toLocaleString()} records.`;
}

/**
 * What this county can evidence, said plainly beside the price.
 *
 * Derived from the county's own signal list rather than from a list of county
 * names — `scoreScope.ts` and `countyDepth.ts`' rule, and the masthead has
 * already gone stale once by hard-coding what a county could not do.
 *
 * It never claims a feed *covers* a county, because none does: Broward's
 * permit register is Fort Lauderdale's, Hillsborough's cases are the
 * unincorporated county's, and even Miami-Dade is missing every
 * municipality's own code compliance.
 */
export function depthNote(quote: Quote): string {
  const count = quote.signals.length;
  const excluded = quote.excluded_signals ?? [];
  if (count === 0) {
    if (excluded.length > 0) {
      // Every declared signal rests on a feed that has stopped: Bay's roll is
      // a February 2022 snapshot and is the county's whole evidence. This is
      // a different statement from "publishes nothing" — the records carry
      // the signals; they are not counted towards what the county is charged
      // as evidencing.
      return (
        `${quote.county_name} evidences no signal that is counted here: ` +
        `${excludedClause(excluded)}`
      );
    }
    return (
      `${quote.county_name} publishes nothing we can score against, so these ` +
      `records carry the parcel roll and the milestone standing and no ` +
      `distress signals at all.`
    );
  }
  const counted =
    count === 1
      ? `${quote.county_name} evidences one signal (${quote.signals[0]}), so ` +
        `every scored record here carries the same score.`
      : `${quote.county_name} evidences ${count} signals on these records.`;
  if (excluded.length === 0) return counted;
  return `${counted} ${excludedClause(excluded)}`;
}

/**
 * "Not counted: code enforcement, no recent permit — every feed they rest on
 * has stopped being written to." The same claim lib/sources.ts makes about a
 * feed, applied to the signals that rest on it: when the county last *wrote*,
 * never whether the records are wrong.
 */
function excludedClause(excluded: string[]): string {
  const names = excluded.map((s) => s.replace(/_/g, " ")).join(", ");
  const rest = excluded.length === 1 ? "it rests on has" : "they rest on have";
  return (
    `Not counted: ${names} — every feed ${rest} stopped being written to, ` +
    `so ${excluded.length === 1 ? "it is" : "they are"} not charged for. ` +
    `The records still carry ${excluded.length === 1 ? "it" : "them"} where ` +
    `${excluded.length === 1 ? "it was" : "they were"} found.`
  );
}
